import { z } from "zod";
import { optionalRequisitionRefSchema } from "./labportalen";

// TES-997 (Ö-259, Ö-247, Ö-255; Emelie 2026-09-29 in TES-996) — the control
// step "Slutför i Webdoc innan du publicerar".
//
// There is no Webdoc integration, so Ella cannot see that a prescription was
// written or a remiss placed. The doctor says so, once, at the moment he
// publishes, and exactly one of three prescription choices is required
// wherever a prescription can be decided:
//
//   "Nytt recept är utfärdat i Webdoc"      → the ONLY choice that sets
//       prescribedAt / prescribedByDoctorId, creates a new PrescriptionCycle
//       and activates the patient's pickup task (medication_pickup_required,
//       CP-12 variant A, TES-903);
//   "Befintligt recept gäller fortfarande"  → the continued plan is published,
//       no new cycle, no pickup task;
//   "Recept kan inte utfärdas ännu"         → no cycle.
//
// Ö-247:
// "Autospar, avslutat videomöte, sammanställd journal eller enbart"
// "publicerad återkoppling får aldrig omvandla ett utkast till utfärdat recept."
// The rule is enforced in the API (PrescriptionCycleService.approve), not by
// the admin form: every route that can write prescribedAt takes this choice.

export const PRESCRIPTION_CHOICES = [
  "new_issued",
  "existing_valid",
  "not_yet",
] as const;
export const prescriptionChoiceSchema = z.enum(PRESCRIPTION_CHOICES);
export type PrescriptionChoice = z.infer<typeof prescriptionChoiceSchema>;

// Her words, verbatim (Ö-259).
export const PRESCRIPTION_CHOICE_LABEL: Record<PrescriptionChoice, string> = {
  new_issued: "Nytt recept är utfärdat i Webdoc",
  existing_valid: "Befintligt recept gäller fortfarande",
  not_yet: "Recept kan inte utfärdas ännu",
};

export const WEBDOC_CONTROL_TITLE = "Slutför i Webdoc innan du publicerar";
// TES-1181 (TES-1180 point 5) — shown only when a new blood test is asked for.
// TES-1315 — A-02 2026-10-13, STEG 4, verbatim:
// "Jag bekräftar att provbeställning/remiss för blodprov är skapad i Webdoc: endast när nytt blodprov har begärts."
export const WEBDOC_LAB_ORDER_LABEL =
  "Jag bekräftar att provbeställning/remiss för blodprov är skapad i Webdoc";
export const PUBLISH_TO_PATIENT_CTA = "Bekräfta och publicera till patienten";
// Ö-255 — the structured date for the prescription the patient already has.
export const EXISTING_PRESCRIPTION_VALID_UNTIL_LABEL =
  "Befintligt recept gäller till";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date_required");

// The doctor's prescription choice, as every publish that can decide a
// prescription sends it. `existingValidUntil` (Ö-255, "när det är känt") only
// belongs to "Befintligt recept gäller fortfarande": a date beside a new or a
// not-yet prescription would describe a prescription nobody chose.
export const prescriptionDecisionSchema = z
  .object({
    choice: prescriptionChoiceSchema,
    existingValidUntil: isoDate.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.existingValidUntil && v.choice !== "existing_valid") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["existingValidUntil"],
        message: "valid_until_only_with_existing_prescription",
      });
    }
  });
export type PrescriptionDecision = z.infer<typeof prescriptionDecisionSchema>;

/** Ö-247 — the one choice that turns an order into an issued prescription. */
export function issuesPrescription(
  decision: Pick<PrescriptionDecision, "choice"> | null | undefined,
): boolean {
  return decision?.choice === "new_issued";
}

/**
 * TES-903 — the explicit `medication_pickup_required` value a choice implies,
 * written on the cycle it lands on. A new prescription must be collected
 * (variant A); a prescription she already holds need not (variant B); no
 * prescription at all has no pickup either way, so it stays unknown (null),
 * which CP-12 reads as its neutral text.
 */
export function medicationPickupRequiredFor(
  decision: Pick<PrescriptionDecision, "choice"> | null | undefined,
): boolean | null {
  if (!decision) return null;
  if (decision.choice === "new_issued") return true;
  if (decision.choice === "existing_valid") return false;
  return null;
}

// Ö-259 — the Webdoc confirmation. Shown ONLY when "Nytt blodprov" was
// chosen in the completion picker:
// "Kompletteringsväljaren avgör behovet;"
// "slutkontrollen bekräftar bara att åtgärden är utförd i Webdoc."
// Since TES-1034 the monthly review asks for it as well, when its
// komplettering chose a new blood test (monthlyReviewNeedsLabOrder).
//
// TES-1019 (Emelie, point 7) — and, on the care meeting's publish, that the
// note itself is in Webdoc:
// "Före publicering måste Kirill bekräfta: ”Journalanteckning och ordination är införda i Webdoc”".
// Required by documentMeetingSchema and, since TES-1034, by the API on the
// monthly review's decisions (approve, reject, publish feedback); the other
// publish routes ignore it.
export const webdocControlSchema = z.object({
  labOrderDoneInWebdoc: z.boolean().optional(),
  journalInWebdoc: z.boolean().optional(),
  // TES-1071 — Steg B's two further ticks, on a positive treatment decision
  // only (see positiveDecisionTicksConfirmed).
  positiveDecisionDocumented: z.boolean().optional(),
  plannedOrderDocumented: z.boolean().optional(),
  // TES-442 — the remiss number the doctor may type beside
  // "Provbeställning/remiss är klar i Webdoc". Optional, never required;
  // stored on the case's open LabOrder.
  labOrderRequisitionRef: optionalRequisitionRefSchema.optional(),
});
export type WebdocControl = z.infer<typeof webdocControlSchema>;

/** The refinement message every publish schema uses for a missing tick. */
export const WEBDOC_LAB_ORDER_UNCONFIRMED = "webdoc_lab_order_unconfirmed";

export function webdocLabOrderConfirmed(
  control: WebdocControl | null | undefined,
): boolean {
  return control?.labOrderDoneInWebdoc === true;
}

// TES-1019 — the journal tick, and the refusal for a publish without it.
// TES-1181 (TES-1180 point 5) — A-02 2026-10-08 replaced the combined
// "Journalanteckning och ordination är införda i Webdoc":
// "En generell kontroll ”Journalanteckning och ordination är införda i Webdoc” får inte visas när ingen ordination finns."
// "Alla utfall kräver ”Journalanteckningen är införd i Webdoc”."
// A prescription gets its own tick (WEBDOC_E_PRESCRIPTION_LABEL).
// TES-1315 — A-02 2026-10-13, STEG 4, verbatim:
// "Jag bekräftar att journalanteckningen är införd i Webdoc: alltid innan publicering."
export const WEBDOC_JOURNAL_LABEL =
  "Jag bekräftar att journalanteckningen är införd i Webdoc";
export const WEBDOC_JOURNAL_UNCONFIRMED = "webdoc_journal_unconfirmed";

export function webdocJournalConfirmed(
  control: WebdocControl | null | undefined,
): boolean {
  return control?.journalInWebdoc === true;
}

// TES-1071 (TES-1069 S2, point 3) — A-02 Steg B, the positive medical
// decision. Her words, verbatim (A-02, "KANONISKT ÖVERORDNAT ADMINFLÖDE"):
// on approval the generic journal tick reads as Steg B's first, and two more
// are required. `journalInWebdoc` carries the first, so every reader of that
// tick keeps its meaning.
// TES-1181 — one wording for the journal tick on every surface.
export const DECISION_JOURNAL_IN_WEBDOC_LABEL = WEBDOC_JOURNAL_LABEL;
export const POSITIVE_DECISION_DOCUMENTED_LABEL =
  "Det positiva medicinska beslutet är dokumenterat";
export const PLANNED_ORDER_DOCUMENTED_LABEL =
  "Planerad ordination är dokumenterad";
// TES-1069 point 4, verbatim: the positive decision's one button.
export const PUBLISH_DECISION_AND_SEND_LINK_CTA =
  "Publicera behandlingsbeslut och skicka abonnemangslänk via e-post";

// TES-1185 (TES-1180 points 1, 2 and 4) — A-02 2026-10-08, "SLUTLIG
// KANONISK REGEL — JOURNALFÖRING, PLANERAD LÄKEMEDELSBEHANDLING OCH RECEPT
// VID KOMPLETTERING": once the evidence is complete Kirill "bekräftar separat
// Journalanteckningen är införd i Webdoc och Receptet är utfärdat i Webdoc.
// Därefter publiceras det positiva behandlingsbeslutet". So a positive
// treatment decision carries the prescription status itself; the choice
// "Receptet är utfärdat i Webdoc" IS the tick (as TES-1150's step's choice
// was). Her words, verbatim.
// TES-1315 — A-02 2026-10-13, STEG 4, verbatim:
// "Jag bekräftar att e-receptet är utfärdat i Webdoc: när patienten är godkänd och ett nytt e-recept ska utfärdas, även när beslutet fattas direkt efter första vårdmötet."
// "E-recept och blodprovsremiss är olika åtgärder och får aldrig benämnas likadant."
// On a new approval this is its own tick, before the journal tick (the note
// journals it; Simon 2026-10-13); ticked, it sends
// `prescription.choice = "new_issued"`.
export const WEBDOC_E_PRESCRIPTION_LABEL =
  "Jag bekräftar att e-receptet är utfärdat i Webdoc";
export const PRESCRIPTION_ISSUED_IN_WEBDOC_LABEL = WEBDOC_E_PRESCRIPTION_LABEL;

/** The refusal for a prescription choice sent with a decision (TES-1071). */
export const PRESCRIPTION_NOT_AT_DECISION = "prescription_not_at_decision";

/**
 * TES-1071 — Steg B's ticks. The planned order is required on every
 * approval today (TES-636), so its tick is asked whenever the approval
 * carries one: `hasPlannedOrder` is the caller's statement of that.
 */
export function positiveDecisionTicksConfirmed(
  control: WebdocControl | null | undefined,
  hasPlannedOrder: boolean,
): boolean {
  return (
    control?.journalInWebdoc === true &&
    control.positiveDecisionDocumented === true &&
    (!hasPlannedOrder || control.plannedOrderDocumented === true)
  );
}
